import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import TrendingSection from "../components/TrendingSection";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";
import ScrollSeparationModel from "../components/ScrollSeparationModel";

gsap.registerPlugin(ScrollTrigger);

const ingredientsList = [
  "Sodium phosphate",
  "Sodium chloride",
  "Glycerin",
  "Benzyl alcohol",
  "Phenol",
  "Hydrochloric acid solution",
  "Sodium hydroxide solution",
  "Water for injection",
];

const AnimatedIngredient = ({ text }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="cursor-pointer"
    >
      <motion.span
        animate={{ color: isHovered ? "#000000" : "#9CA3AF" }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
      >
        {text}
      </motion.span>
      <motion.span
        className="text-black"
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: isHovered ? 1 : 0, x: isHovered ? 0 : -10 }}
        transition={{ duration: 1, ease: "easeOut", delay: 0.1 }}
      >
        .
      </motion.span>
    </motion.div>
  );
};

const TiltCard = ({ children, className }) => {
  const cardRef = useRef(null);
  const rotationRef = useRef({ x: 0, y: 0 });
  const rectRef = useRef(null);
  const rafRef = useRef(null);
  const isHoveringRef = useRef(false);
  const initialTopRef = useRef(0);

  const updateTransform = () => {
    if (!cardRef.current || !isHoveringRef.current) return;

    const { x, y } = rotationRef.current;
    const card = cardRef.current;

    const rotateXRad = (x * Math.PI) / 180;
    const height = rectRef.current?.height || card.offsetHeight;
    const compensateY =
      (height / 2) * Math.sin(rotateXRad) * (1 - Math.cos(rotateXRad));

    card.style.transform = `perspective(1000px) translateZ(0) translateY(${-compensateY}px) rotateX(${x}deg) rotateY(${y}deg)`;

    rafRef.current = null;
  };

  const handleMouseMove = (e) => {
    const card = cardRef.current;
    if (!card) return;

    if (!rectRef.current) {
      rectRef.current = card.getBoundingClientRect();
      initialTopRef.current = rectRef.current.top;
    }

    const { clientX, clientY } = e;
    const { left, top, width, height } = rectRef.current;

    const x = clientX - left;
    const y = clientY - top;

    const rotateX = (y / height - 0.5) * -20;
    const rotateY = (x / width - 0.5) * 20;

    rotationRef.current = { x: rotateX, y: rotateY };

    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(updateTransform);
    }
  };

  const handleMouseLeave = () => {
    isHoveringRef.current = false;
    rotationRef.current = { x: 0, y: 0 };
    rectRef.current = null;
    initialTopRef.current = 0;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (cardRef.current) {
      cardRef.current.style.transform =
        "perspective(1000px) translateZ(0) translateY(0px) rotateX(0deg) rotateY(0deg)";
    }
  };

  const handleMouseEnter = () => {
    isHoveringRef.current = true;
    if (cardRef.current) {
      rectRef.current = cardRef.current.getBoundingClientRect();
      initialTopRef.current = rectRef.current.top;
    }
  };

  return (
    <div
      ref={cardRef}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={className}
      style={{
        transition: "transform 0.4s ease-out",
        transformOrigin: "center center",
        transformStyle: "preserve-3d",
        willChange: "transform",
      }}
    >
      {children}
    </div>
  );
};

export default function About() {
  const modelSectionRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray(".animated-section").forEach((section) => {
        gsap.from(section.children, {
          y: 50,
          opacity: 0,
          duration: 0.8,
          ease: "power3.out",
          stagger: 0.2,
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            toggleActions: "play none none none",
          },
        });
      });
    });
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const section = modelSectionRef.current;
    if (!section) return;

    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const inView = rect.top < window.innerHeight && rect.bottom > 0;

      const currentScrollY = window.scrollY;
      const direction = currentScrollY > lastScrollY ? "forward" : "backward";
      lastScrollY = currentScrollY;

      if (inView) {
        window.dispatchEvent(
          new CustomEvent("triggerModelAnimation", {
            detail: { direction },
          })
        );
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="animated-section px-6 md:px-20 py-32 md:py-32 h-auto md:h-screen flex items-center justify-center">
        <h1 className="text-[80px] sm:text-[100px] md:text-[150px] lg:text-[320px] font-bold mb-12 leading-none text-center">
          Est. 2022
        </h1>
      </section>

      {/* Thinking */}
      <section className="animated-section px-6 md:px-20 py-12 md:py-16 text-center mb-12 md:mb-16">
        <h2 className="text-xl md:text-2xl font-bold text-black mb-8 md:mb-16">
          The Thinking Behind <br /> Mounjaro
        </h2>
        <div className="space-y-8 md:space-y-12 text-lg md:text-2xl text-black/60 leading-relaxed text-center max-w-2xl mx-auto px-4 md:px-0">
          <p>
            <strong className="text-black">Mounjaro (tirzepatide)</strong> was
            invented to improve{" "}
            <strong className="text-black">blood sugar</strong> control in
            adults with type <strong className="text-black">2 diabetes</strong>.
          </p>
          <p>
            It works by mimicking{" "}
            <strong className="text-black">two hormones</strong> that regulate
            blood sugar, <strong className="text-black">GLP-1 and GIP</strong>.
          </p>
          <p>
            <strong className="text-black">Additionally,</strong> Mounjaro can
            also contribute to weight loss through appetite and metabolism
            effects.
          </p>
        </div>
      </section>

      {/* 3D Model */}
      <section
        ref={modelSectionRef}
        className="animated-section px-6 md:px-20 py-10 md:py-6 h-[40vh] md:h-[85vh] relative flex items-center justify-center bg-black"
      >
        <ScrollSeparationModel />
      </section>

      {/* Stats */}
      <section className="animated-section px-6 md:px-20 py-12 md:py-16 grid grid-cols-1 md:grid-cols-8 gap-4 lg:gap-6">
        <div className="md:col-span-4 flex items-top mb-8 md:mb-0">
          <h3 className="text-3xl lg:text-5xl font-bold text-black text-left">
            Pioneering the<br />
            Future of Weight<br />
            Management
          </h3>
        </div>

        <TiltCard className="md:col-span-4 bg-gray-100 p-6 md:p-8 h-auto lg:h-80 text-left flex flex-col justify-between">
          <div>
            <div className="text-5xl lg:text-8xl font-semibold text-black mb-4">
              96%
            </div>
          </div>
          <p className="text-base lg:text-lg text-gray-600 mt-4 w-full lg:w-1/3">
            Of people taking Mounjaro lost weight in clinical studies
          </p>
        </TiltCard>

        <TiltCard className="md:col-span-5 bg-blue-200 p-6 md:p-8 h-auto lg:h-80 text-left flex flex-col justify-between">
          <div>
            <div className="text-4xl lg:text-7xl font-semibold text-black">
              24
            </div>
            <div className="text-4xl lg:text-7xl font-semibold text-black">
              Available
            </div>
          </div>
          <p className="text-base lg:text-lg text-gray-600 mt-4 w-full lg:w-1/3">
            Approved in major markets including the US, UK, and UAE
          </p>
        </TiltCard>

        <TiltCard className="md:col-span-3 bg-[#2c3e50] p-6 md:p-8 text-white h-auto lg:h-80 text-left flex flex-col justify-between">
          <div>
            <div className="text-5xl lg:text-8xl font-semibold text-white mb-4">
              1x
            </div>
          </div>
          <p className="text-base lg:text-lg text-gray-400 mt-4 w-full lg:w-1/2">
            Weekly injection for convenience and consistent results
          </p>
        </TiltCard>

        <TiltCard className="md:col-span-4 bg-[#2c3e50] p-6 md:p-8 text-white h-auto lg:h-80 text-center flex flex-col justify-between">
          <div>
            <div className="text-2xl lg:text-4xl font-medium text-white mb-4">
              Studies show around 1% weight loss a week for the first 4 weeks
            </div>
          </div>
          <p className="text-base lg:text-lg">Mounjaro</p>
        </TiltCard>

        <TiltCard className="md:col-span-4 bg-white p-6 md:p-8 border h-auto lg:h-80 text-left flex flex-col justify-between">
          <div>
            <div className="text-3xl lg:text-5xl font-bold text-black mb-4">
              Addressing a Global Health Crisis
            </div>
          </div>
          <p className="text-base lg:text-lg text-gray-600 w-full lg:w-1/3">
            Obesity affects millions worldwide. Mounjaro offers a new path forward.
          </p>
        </TiltCard>
      </section>

      {/* Ingredients */}
      <section className="animated-section px-6 md:px-20 py-12 md:py-16 bg-gray-100 text-left">
        <h2 className="text-xl md:text-2xl font-bold text-black mb-8 md:mb-12">
          Ingredients of Mounjaro
        </h2>
        <div className="space-y-4 md:space-y-6 text-3xl md:text-6xl">
          {ingredientsList.map((ingredient, idx) => (
            <AnimatedIngredient key={idx} text={ingredient} />
          ))}
        </div>
      </section>

      <TrendingSection />
    </div>
  );
}
